import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";
import i18n from "@/i18n";
import { z } from "zod";

// Enum options extracted from Ruby enum A3Metadata.field_key
export const fieldKeyOptions = [
  { value: "gps", label: "GPS" },
  { value: "management_mode", label: "Management Mode" },
  { value: "strategy_code", label: "Strategy Code" },
  { value: "custodian_bank_name", label: "Custodian Bank Name" },
  { value: "counterpart_code", label: "Counterpart Code" },
  { value: "counterpart_name", label: "Counterpart Name" },
];

export const filterA3MetadataSchema = z.object({
  owner_type_contains: z.string().nullable().optional(),
  owner_id_contains: z
    .preprocess((val: unknown) => {
      // if the form returns a string, coerce to number
      const n = Number(val);
      return isNaN(n) ? undefined : n;
    }, z.number())
    .optional(),
  field_value_contains: z.string().nullable().optional(),
  field_key_contains: z.string().nullable().optional(),
  created_at_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  created_at_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  updated_at_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  updated_at_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

export type FilterA3MetadataSchema = z.infer<typeof filterA3MetadataSchema>;

export const fields: Field[] = [
  {
    name: "owner_type_contains",
    label: i18n.t("a3_metadata.columns.owner_type", {
      ns: "tables",
      defaultValue: "Owner Type",
    }),
    type: "input",
  },
  {
    name: "owner_id_contains",
    label: i18n.t("a3_metadata.columns.owner_id", {
      ns: "tables",
      defaultValue: "Owner ID",
    }),
    type: "input",
    input_type: "number",
  },
  {
    name: "field_value_contains",
    label: i18n.t("a3_metadata.columns.field_value", {
      ns: "tables",
      defaultValue: "Field Value",
    }),
    type: "input",
  },
  {
    name: "field_key_contains",
    label: i18n.t("a3_metadata.columns.field_key", {
      ns: "tables",
      defaultValue: "Field Key",
    }),
    type: "select",
    useTranslation: false,
    options: fieldKeyOptions,
  },
  {
    name: "created_at_from",
    label: "Created At From",
    type: "date",
  },
  {
    name: "created_at_to",
    label: "Created At To",
    type: "date",
  },
  {
    name: "updated_at_from",
    label: "Updated At From",
    type: "date",
  },
  {
    name: "updated_at_to",
    label: "Updated At To",
    type: "date",
  },
];

export const client = "a3";

export function FilterA3Metadata() {
  return (
    <FilterTable
      client={client}
      schema={filterA3MetadataSchema}
      tableName="a3_metadata"
      customBuildPath={() => `${client}/a3_metadata`}
      fields={fields}
    />
  );
}
